import { useState } from 'react';
import { Box, Checkbox, Collapse, Divider, FormControlLabel, Link, Typography } from '@mui/material';
import {
  CarrierServicingGrantInputDto,
  KioskTransactionType,
  ProjectDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { LoadingIndicator } from '../../../../ui/components/pure/loading-indicator/loading-indicator.component';
import {
  ADVANCED_TRANSACTION_TYPES,
  DEFAULT_SERVICING_PERMISSION,
  formatTransactionType,
  getNetworkPermissions,
  setNetworkPermissions,
  toggleNetworkSelection
} from '../../logic/add-carrier-form';

export interface NetworksStepProps {
  networks: ProjectDto[];
  isLoading: boolean;
  /** Set when the networks could not be loaded, so an empty list is not reported as "none exist". */
  loadError?: string;
  servicing: CarrierServicingGrantInputDto[];
  onChange: (servicing: CarrierServicingGrantInputDto[]) => void;
  disabled: boolean;
}

export function NetworksStep({ networks, isLoading, loadError, servicing, onChange, disabled }: NetworksStepProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        A carrier can only work in the networks selected here. This can be changed later, but a carrier with no networks
        selected cannot use any kiosks.
      </Typography>
      {isLoading && <LoadingIndicator />}
      {!isLoading && loadError && (
        <Typography variant="body2" color="error">
          {loadError}
        </Typography>
      )}
      {!isLoading && !loadError && networks.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          There are no networks available.
        </Typography>
      )}
      {networks.map((network) => (
        <NetworkRow
          key={network.id}
          network={network}
          permissions={getNetworkPermissions(servicing, network.id)}
          selected={servicing.some((grant) => grant.projectID === network.id)}
          disabled={disabled}
          onToggle={() => onChange(toggleNetworkSelection(servicing, network.id))}
          onPermissionsChange={(permissions) => onChange(setNetworkPermissions(servicing, network.id, permissions))}
        />
      ))}
    </Box>
  );
}

function NetworkRow({
  network,
  permissions,
  selected,
  disabled,
  onToggle,
  onPermissionsChange
}: {
  network: ProjectDto;
  permissions: KioskTransactionType[];
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
  onPermissionsChange: (permissions: KioskTransactionType[]) => void;
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const togglePermission = (type: KioskTransactionType) => {
    onPermissionsChange(permissions.includes(type) ? permissions.filter((p) => p !== type) : [...permissions, type]);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControlLabel
          control={<Checkbox checked={selected} onChange={onToggle} disabled={disabled} color="primary" />}
          label={network.name}
        />
        {selected && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setAdvancedOpen((open) => !open)}
            disabled={disabled}
          >
            {advancedOpen ? 'Hide advanced' : 'Advanced'}
          </Link>
        )}
      </Box>
      {selected && (
        <Collapse in={advancedOpen} unmountOnExit>
          <Box
            sx={{
              paddingLeft: 4,
              paddingBottom: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="caption" color="textSecondary">
              What this carrier may do in {network.name}.
            </Typography>
            <PermissionCheckbox
              type={DEFAULT_SERVICING_PERMISSION}
              permissions={permissions}
              disabled={disabled}
              onToggle={togglePermission}
            />
            {ADVANCED_TRANSACTION_TYPES.map((type) => (
              <PermissionCheckbox
                key={type}
                type={type}
                permissions={permissions}
                disabled={disabled}
                onToggle={togglePermission}
              />
            ))}
            {permissions.length === 0 && (
              <Typography variant="caption" color="error">
                Select at least one, or clear this network.
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
      <Divider />
    </Box>
  );
}

function PermissionCheckbox({
  type,
  permissions,
  disabled,
  onToggle
}: {
  type: KioskTransactionType;
  permissions: KioskTransactionType[];
  disabled: boolean;
  onToggle: (type: KioskTransactionType) => void;
}) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          color="primary"
          checked={permissions.includes(type)}
          onChange={() => onToggle(type)}
          disabled={disabled}
        />
      }
      label={<Typography variant="body2">{formatTransactionType(type)}</Typography>}
    />
  );
}
